import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject, Subscription, switchMap } from 'rxjs'
import { LocalChannel } from '../interfaces/local-channel'
import { ChannelPlan } from '../interfaces/channel-plan'
import { ChannelPlanType, ChannelType } from '../interfaces/channel'
import { LogService } from 'htv-lib'
import { MediaService } from './media.service'
import { ChannelPosition } from '../pages/channel/channel-position'
import { PlatformService } from '../platforms/platform.service'
import { ChannelTrackData, CurrentChannelData } from '../interfaces/channel-track-data'
import { EpgService } from './epg.service'
import { LocalChannelLiveEPG } from '../interfaces/local-epg'
import { RouterPage, RouterService } from './router.service'
import { safeStringify } from '../utils/safe-stringify'

export const RCU_PRESSED_DEBOUNCE = 500

@Injectable({
  providedIn: 'root',
})
export class ChannelService {
  keyDownSubscription?: Subscription
  isLoading$ = new BehaviorSubject<boolean>(true)

  channelPlans = new Map<ChannelPlanType, LocalChannel[]>()
  channelPlanType: ChannelPlanType = ChannelPlanType.DEFAULT
  channels: LocalChannel[] = []

  // Channel selected to be displayed but not necessarily loaded
  selectedChannel?: LocalChannel
  selectedChannel$ = new BehaviorSubject<LocalChannel | undefined>(undefined)

  // Channel currently loaded and displayed
  // Widgets should use this subject to retrieve data about current channel
  currentChannel?: LocalChannel
  currentChannel$ = new Subject<LocalChannel | undefined>()

  channelListenerSubject = new BehaviorSubject<boolean>(false)
  channelListenerSubscription?: Subscription

  pollChannelDataTimer: any
  readonly pollChannelDataFailureDelayInMs = 1000
  readonly pollChannelDataSuccessDelayInMs = 10000

  pollLiveEPGTimer: any
  readonly pollLiveEPGTimerDelayInMs = 10000

  constructor(
    private epgService: EpgService,
    private logService: LogService,
    private mediaService: MediaService,
    private platformService: PlatformService,
    private routerService: RouterService
  ) {
    // do nothing
  }

  init(channelPlan?: ChannelPlan): void {
    this.isLoading$.next(true)
    this.logService.debug('[ChannelService] Initializing with Humecloud data')

    // Reset
    this.channels = []
    this.channelPlans.clear()

    // Load channel listener
    this.registerChannelListener()

    // Load channel plans from Humecloud
    if (channelPlan && channelPlan.channels) {
      channelPlan.channels.forEach(channel => {
        const localChannel: LocalChannel = {
          id: channel.number.toString(),
          globalChannelId: channel.source.globalChannelId,
          name: channel.name,
          type: channel.source.configuredChannelType,
          source: channel.source,
          logoUrl: this.mediaService.getMediaUrl(channel.iconMediaId) ?? '',
          channelData$: new BehaviorSubject<CurrentChannelData | undefined>(undefined),
          liveEPG$: new BehaviorSubject<LocalChannelLiveEPG | undefined>(undefined),
        }

        channel.source.planTypes.forEach(channelPlanType => {
          if (!this.channelPlans.has(channelPlanType)) {
            this.channelPlans.set(channelPlanType, [])
          }
          this.channelPlans.get(channelPlanType)?.push(localChannel)
        })

        this.logService.debug(`[ChannelService] Loaded channel ${localChannel.id} ${localChannel.name}`)
      })

      this.refreshChannelPlan()
    } else {
      this.logService.log('[ChannelService] Initializing with empty channelPlan')
      this.channelPlanType = ChannelPlanType.DEFAULT
      this.channels = []
    }

    this.isLoading$.next(false)
  }

  registerChannelListener(): void {
    if (this.channelListenerSubscription) {
      this.channelListenerSubscription?.unsubscribe()
    }
    this.channelListenerSubject.next(false)
    this.channelListenerSubscription = this.channelListenerSubject
      .pipe(
        switchMap(async (isChannelChanged: boolean) => {
          if (isChannelChanged) {
            try {
              return await this.platformService.getCurrentChannel()
            } catch (err: any) {
              // do nothing
            }
          }
        })
      )
      .subscribe((currentChannel: any) => {
        if (currentChannel) {
          this.pollChannelData()
        }
      })
    this.platformService.registerChannelListener(this.channelListenerSubject)
  }

  getChannel(id: string): LocalChannel {
    let selectedChannel: LocalChannel
    const index = this.channels.findIndex(c => c.id === id)
    if (index > -1) {
      selectedChannel = this.channels[index]
    } else {
      selectedChannel = this.channels[0]
    }
    return selectedChannel
  }

  getExactChannel(id: string): LocalChannel | undefined {
    const index = this.channels.findIndex(c => c.id === id)
    if (index > -1) {
      return this.channels[index]
    } else {
      return undefined
    }
  }

  getChannelByGlobalChannelId(id: string): LocalChannel | undefined {
    const index = this.channels.findIndex(c => c.globalChannelId === id)
    if (index > -1) {
      return this.channels[index]
    } else {
      this.logService.info(`[ChannelService] Could not find channel with global ID: ${id}`)
      return undefined
    }
  }

  getPendingChannel(): LocalChannel {
    if (this.selectedChannel) {
      return this.selectedChannel
    } else {
      return this.getNextChannel()
    }
  }

  getNextChannel(channel?: LocalChannel): LocalChannel {
    let selectedChannel: LocalChannel | undefined

    const findChannel = (channelId: string): LocalChannel | undefined => {
      const index = this.channels.findIndex(c => c.id === channelId)
      if (index > -1) {
        return this.channels[index + 1]
      } else {
        return undefined
      }
    }

    if (channel) {
      selectedChannel = findChannel(channel.id)
    } else if (this.selectedChannel) {
      selectedChannel = findChannel(this.selectedChannel.id)
    }
    if (!selectedChannel) {
      selectedChannel = this.channels[0]
    }
    return selectedChannel
  }

  getPreviousChannel(channel?: LocalChannel): LocalChannel {
    let selectedChannel: LocalChannel | undefined

    const findChannel = (channelId: string): LocalChannel | undefined => {
      const index = this.channels.findIndex(c => c.id === channelId)
      if (index > -1) {
        if (index > 0) {
          return this.channels[index - 1]
        } else {
          return this.channels[this.channels.length - 1]
        }
      } else {
        return undefined
      }
    }

    if (channel) {
      selectedChannel = findChannel(channel.id)
    } else if (this.selectedChannel) {
      selectedChannel = findChannel(this.selectedChannel.id)
    }
    if (!selectedChannel) {
      selectedChannel = this.channels[0]
    }
    return selectedChannel
  }

  selectChannel(channel: LocalChannel | undefined): void {
    this.selectedChannel = channel
    this.selectedChannel$.next(channel)
  }

  openChannelPage(channel: LocalChannel): void {
    this.selectChannel(channel)

    if (!this.routerService.isCurrentPage(RouterPage.CHANNEL)) {
      this.routerService.goToPage(RouterPage.CHANNEL)
    }
  }

  startChannel(channel: LocalChannel, pos: ChannelPosition): void {
    if (this.currentChannel) {
      this.stopChannel()
    }

    this.logService.info(`[ChannelService] [${channel.source.channelNumber}] ${channel.name} (${channel.type})`)
    this.logService.debug(`[ChannelService] ${safeStringify(channel.source)}`)

    switch (channel.type) {
      case ChannelType.DVBT:
        this.platformService.startBroadcastChannel(channel, pos)
        break
      case ChannelType.HDMI:
        this.logService.error(`HDMI channel type is not supported`)
        return
      case ChannelType.UDP:
        this.platformService.startUDPChannel(channel, pos)
        break
      case ChannelType.WEB:
        this.platformService.startWebChannel(channel, pos)
        break
      default:
        return
    }

    this.currentChannel = channel
    this.currentChannel$.next(channel)

    this.pollLiveEPG()
  }

  stopChannel(): void {
    if (this.pollChannelDataTimer) {
      clearTimeout(this.pollChannelDataTimer)
    }
    if (this.pollLiveEPGTimer) {
      clearTimeout(this.pollLiveEPGTimer)
    }

    if (this.currentChannel) {
      switch (this.currentChannel?.type) {
        case ChannelType.DVBT:
          this.platformService.stopBroadcastChannel()
          break
        case ChannelType.HDMI:
          this.logService.error(`HDMI channel type is not supported`)
          break
        case ChannelType.UDP:
          this.platformService.stopUDPChannel()
          break
        case ChannelType.WEB:
          this.platformService.stopWebChannel()
          break
        default:
          break
      }

      this.currentChannel.channelData$.next(undefined)
      this.currentChannel.liveEPG$.next(undefined)
    }

    this.currentChannel = undefined
    this.currentChannel$.next(undefined)
  }

  async pollChannelData(): Promise<void> {
    if (this.pollChannelDataTimer) {
      clearTimeout(this.pollChannelDataTimer)
    }

    if (!this.currentChannel) {
      return
    }

    const registerPollChannelDataTimer = (delay: number) => {
      if (this.pollChannelDataTimer) {
        clearTimeout(this.pollChannelDataTimer)
      }
      this.pollChannelDataTimer = setTimeout(() => {
        this.pollChannelData()
      }, delay)
    }

    try {
      let channelData: CurrentChannelData | undefined = undefined
      if (this.currentChannel) {
        switch (this.currentChannel?.type) {
          case ChannelType.DVBT:
            channelData = await this.platformService.getBroadcastCurrentProgramData()
            break
          case ChannelType.HDMI:
            this.logService.error(`HDMI channel type is not supported`)
            break
          case ChannelType.UDP:
            channelData = await this.platformService.getUDPCurrentProgramData()
            break
          case ChannelType.WEB:
            channelData = await this.platformService.getWebCurrentProgramData()
            break
          default:
            break
        }
      }

      if (channelData) {
        this.logService.debug(`[ChannelService] Current channel data: ${JSON.stringify(channelData)}`)

        registerPollChannelDataTimer(this.pollChannelDataSuccessDelayInMs)

        if (this.currentChannel) {
          this.currentChannel.channelData$.next(channelData)
        } else {
          // TODO: Do we need to do anything ?
        }
      } else {
        this.logService.debug(`[ChannelService] Could not retrieve current channel data`)
        registerPollChannelDataTimer(this.pollChannelDataFailureDelayInMs)
      }
    } catch (err: any) {
      this.logService.debug(
        `[ChannelService] An error occured while retrieving current channel data, error = ${err?.message}`
      )
      registerPollChannelDataTimer(this.pollChannelDataFailureDelayInMs)
    }
  }

  pollLiveEPG(): void {
    if (this.pollLiveEPGTimer) {
      clearTimeout(this.pollLiveEPGTimer)
    }

    const registerPollLiveEPGTimer = (delay: number) => {
      this.pollLiveEPGTimer = setTimeout(() => {
        this.pollLiveEPG()
      }, delay)
    }

    if (this.currentChannel) {
      const liveEPG = this.epgService.getChannelLiveEPG(this.currentChannel.id)
      this.currentChannel.liveEPG$.next(liveEPG)
      registerPollLiveEPGTimer(this.pollLiveEPGTimerDelayInMs)
    } else {
      // do nothing
    }
  }

  setAudioTrack(track: ChannelTrackData): void {
    if (this.currentChannel) {
      switch (this.currentChannel?.type) {
        case ChannelType.DVBT:
          this.platformService.setBroadcastAudioTrack(track)
          break
        case ChannelType.HDMI:
          this.logService.error(`HDMI channel type is not supported`)
          break
        case ChannelType.UDP:
          this.platformService.setUDPAudioTrack(track)
          break
        case ChannelType.WEB:
          this.platformService.setWebAudioTrack(track)
          break
        default:
          break
      }
    }
    this.pollChannelData()
  }

  async setSubtitleState(enable: boolean): Promise<void> {
    await this.platformService.setSubtitleState(enable)
    this.pollChannelData()
  }

  async setSubtitleTrack(track: ChannelTrackData, enable: boolean): Promise<void> {
    await this.platformService.setSubtitleState(enable)
    if (this.currentChannel) {
      switch (this.currentChannel?.type) {
        case ChannelType.DVBT:
          this.platformService.setBroadcastSubtitleTrack(track)
          break
        case ChannelType.HDMI:
          this.logService.error(`HDMI channel type is not supported`)
          break
        case ChannelType.UDP:
          this.platformService.setUDPSubtitleTrack(track)
          break
        case ChannelType.WEB:
          this.platformService.setWebSubtitleTrack(track)
          break
        default:
          break
      }
    }
    this.pollChannelData()
  }

  refreshChannelPlan(): void {
    this.logService.debug(`[ChannelService] Refreshing channel plan for type ${this.channelPlanType}`)

    this.channels = this.channelPlans.get(this.channelPlanType) ?? []

    if (this.channels.length === 0 && this.channelPlanType !== ChannelPlanType.DEFAULT) {
      this.logService.info(
        `[ChannelService] No channels found for plan ${this.channelPlanType}, falling back to DEFAULT`
      )
      this.channelPlanType = ChannelPlanType.DEFAULT
      this.channels = this.channelPlans.get(this.channelPlanType) ?? []
    }

    this.logService.info(`[ChannelService] Loaded ${this.channelPlans.size} Channel Plans`)
    this.channelPlans.forEach((value, key) => {
      if (key === this.channelPlanType) {
        this.logService.info(`[ChannelService] Channel Plan ${key} with ${value.length} channels (current)`)
      } else {
        this.logService.info(`[ChannelService] Channel Plan ${key} with ${value.length} channels`)
      }
    })

    this.selectChannel(undefined)
  }

  determineChannelPlanType(guestMode: boolean): void {
    const isCheckedIn = guestMode
    const isCheckedOut = !guestMode

    if (isCheckedIn && !isCheckedOut) {
      this.channelPlanType = ChannelPlanType.CHKI
    } else if (isCheckedOut && !isCheckedIn) {
      this.channelPlanType = ChannelPlanType.CHKO
    } else {
      this.channelPlanType = ChannelPlanType.DEFAULT
    }

    this.refreshChannelPlan()
  }
}
